import { z } from 'zod';
import { CustomerSapSystemEnum } from './customer.js';
import { InvoiceDocumentTypeEnum } from './invoice-blob.js';

const ActiveSapSystemEnum = z.enum(['sap-s4hana', 'sap-erp-6']);

export const InvoiceSapVendorLookupStatusEnum = z.enum([
  'not_started',
  'matched',
  'not_found',
  'multiple',
  'error',
  'skipped',
]);
export type InvoiceSapVendorLookupStatus = z.infer<typeof InvoiceSapVendorLookupStatusEnum>;

export const InvoiceSapPostingStatusEnum = z.enum([
  'not_started',
  'posted',
  'blocked',
  'error',
  'skipped',
]);
export type InvoiceSapPostingStatus = z.infer<typeof InvoiceSapPostingStatusEnum>;

export const InvoiceSapReleaseStatusEnum = z.enum(['not_started', 'released', 'error', 'skipped']);
export type InvoiceSapReleaseStatus = z.infer<typeof InvoiceSapReleaseStatusEnum>;

export const InvoiceSapSendStatusEnum = z.enum(['not_started', 'sent', 'error', 'skipped']);
export type InvoiceSapSendStatus = z.infer<typeof InvoiceSapSendStatusEnum>;

export const InvoiceSapMaterialLookupStatusEnum = z.enum([
  'not_started',
  'matched',
  'not_found',
  'multiple',
  'error',
  'skipped',
]);
export type InvoiceSapMaterialLookupStatus = z.infer<typeof InvoiceSapMaterialLookupStatusEnum>;
export const InvoiceSapCustomerLookupStatusEnum = InvoiceSapMaterialLookupStatusEnum;
export type InvoiceSapCustomerLookupStatus = z.infer<typeof InvoiceSapCustomerLookupStatusEnum>;

export const InvoiceSapCustomerOrderAttachmentStatusEnum = z.enum([
  'not_started',
  'attached',
  'skipped',
  'error',
]);
export type InvoiceSapCustomerOrderAttachmentStatus = z.infer<
  typeof InvoiceSapCustomerOrderAttachmentStatusEnum
>;

export const InvoiceSapMatchingGateStatusEnum = z.enum([
  'not_started',
  'passed',
  'blocked',
  'warning',
  'error',
]);
export type InvoiceSapMatchingGateStatus = z.infer<typeof InvoiceSapMatchingGateStatusEnum>;

export const InvoiceSapGateCheckStatusEnum = z.enum(['passed', 'blocked', 'warning', 'skipped']);
export type InvoiceSapGateCheckStatus = z.infer<typeof InvoiceSapGateCheckStatusEnum>;

export const InvoiceSapGateCheckSchema = z.object({
  code: z.string(),
  label: z.string(),
  status: InvoiceSapGateCheckStatusEnum,
  message: z.string().optional(),
});
export type InvoiceSapGateCheck = z.infer<typeof InvoiceSapGateCheckSchema>;

export const InvoiceSapVendorMatchSchema = z.object({
  vendorId: z.string(),
  vendorName: z.string(),
  matchedBy: z.enum([
    'vat_id',
    'name',
    'name_and_vat',
    'name_address',
    'name_address_vat',
    'name_address_iban',
  ]),
  street: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().optional(),
  country: z.string().optional(),
  vatId: z.string().optional(),
  iban: z.string().optional(),
});
export type InvoiceSapVendorMatch = z.infer<typeof InvoiceSapVendorMatchSchema>;

export const InvoiceSapMaterialMatchSchema = z.object({
  lineIndex: z.number().int().nonnegative(),
  position: z.string().optional(),
  materialId: z.string(),
  supplierMaterialNumber: z.string().optional(),
  vendorId: z.string().optional(),
  description: z.string().optional(),
});
export type InvoiceSapMaterialMatch = z.infer<typeof InvoiceSapMaterialMatchSchema>;

export const InvoiceSapMaterialIssueSchema = z.object({
  lineIndex: z.number().int().nonnegative(),
  status: z.enum(['not_found', 'multiple']),
  position: z.string().optional(),
  materialNumber: z.string().optional(),
  message: z.string().optional(),
});
export type InvoiceSapMaterialIssue = z.infer<typeof InvoiceSapMaterialIssueSchema>;

export const InvoiceSapPostingSchema = z.object({
  transaction: z.enum([
    'supplier_invoice',
    'billing_draft',
    'purchase_order_update',
    'sales_order_create',
  ]),
  transactionCode: z
    .enum(['FB60', 'FV60', 'MIRO', 'FV70', 'FB70', 'SD_BILLING', 'ME22N', 'VA01'])
    .optional(),
  documentNumber: z.string(),
  blocked: z.boolean().optional(),
  companyCode: z.string().optional(),
  message: z.string().optional(),
});
export type InvoiceSapPosting = z.infer<typeof InvoiceSapPostingSchema>;

export const InvoiceSapCustomerOrderItemSchema = z.object({
  lineIndex: z.number().int().nonnegative(),
  position: z.string().optional(),
  customerMaterialNumber: z.string().optional(),
  material: z.string().optional(),
  requestedQuantity: z.number().optional(),
  unit: z.string().optional(),
  netPrice: z.number().optional(),
  marginPercent: z.number().optional(),
  requestedDeliveryDate: z.string().date().optional(),
  status: z.enum(['matched', 'not_found', 'multiple', 'price_mismatch', 'blocked']),
  message: z.string().optional(),
});
export type InvoiceSapCustomerOrderItem = z.infer<typeof InvoiceSapCustomerOrderItemSchema>;

export const InvoiceSapCustomerOrderSyncSchema = z.object({
  salesOrderType: z.string().default('ZAN1'),
  salesOrderNumber: z.string().optional(),
  purchaseOrderByCustomer: z.string().optional(),
  customerPurchaseOrderDate: z.string().date().optional(),
  soldToParty: z.string().optional(),
  shipToParty: z.string().optional(),
  shippingPlant: z.string().default('1000'),
  salesOrganization: z.string().optional(),
  distributionChannel: z.string().optional(),
  division: z.string().optional(),
  billingBlock: z.string().optional(),
  deliveryBlock: z.string().optional(),
  createStatus: z
    .enum(['not_started', 'created', 'blocked', 'skipped', 'error'])
    .default('not_started'),
  attachmentStatus: InvoiceSapCustomerOrderAttachmentStatusEnum.default('not_started'),
  attachmentDocumentId: z.string().optional(),
  attachmentMessage: z.string().optional(),
  items: z.array(InvoiceSapCustomerOrderItemSchema).default([]),
  issues: z.array(InvoiceSapGateCheckSchema).default([]),
  message: z.string().optional(),
});
export type InvoiceSapCustomerOrderSync = z.infer<typeof InvoiceSapCustomerOrderSyncSchema>;

export const InvoiceSapPurchaseOrderIssueSchema = z.object({
  lineIndex: z.number().int().nonnegative().optional(),
  position: z.string().optional(),
  code: z.enum([
    'missing_order',
    'order_not_found',
    'wrong_order_type',
    'position_not_found',
    'product_mismatch',
    'quantity_mismatch',
    'delivery_address_mismatch',
    'mismatch',
  ]),
  message: z.string(),
});
export type InvoiceSapPurchaseOrderIssue = z.infer<typeof InvoiceSapPurchaseOrderIssueSchema>;

export const InvoiceSapPurchaseOrderItemSchema = z.object({
  lineIndex: z.number().int().nonnegative(),
  position: z.string(),
  purchaseOrder: z.string().optional(),
  purchaseOrderItem: z.string(),
  materialId: z.string().optional(),
  quantity: z.number().optional(),
  netAmount: z.number().optional(),
  matched: z.boolean(),
});
export type InvoiceSapPurchaseOrderItem = z.infer<typeof InvoiceSapPurchaseOrderItemSchema>;

export const InvoiceSapPurchaseOrderSyncSchema = z.object({
  purchaseOrder: z.string().optional(),
  purchaseOrderType: z.string().optional(),
  matchStatus: z.enum(['not_started', 'matched', 'not_found', 'mismatch', 'skipped', 'error']),
  updateStatus: z.enum(['not_started', 'updated', 'skipped', 'error']).default('not_started'),
  transactionCode: z.literal('ME22N').optional(),
  confirmationNumber: z.string().optional(),
  items: z.array(InvoiceSapPurchaseOrderItemSchema).optional(),
  issues: z.array(InvoiceSapPurchaseOrderIssueSchema).optional(),
  message: z.string().optional(),
});
export type InvoiceSapPurchaseOrderSync = z.infer<typeof InvoiceSapPurchaseOrderSyncSchema>;

export const InvoiceSapReleaseSchema = z.object({
  transaction: z.literal('billing_release'),
  transactionCode: z.enum(['FB70', 'SD_BILLING']).optional(),
  documentNumber: z.string(),
  releasedAt: z.string().datetime().optional(),
  message: z.string().optional(),
});
export type InvoiceSapRelease = z.infer<typeof InvoiceSapReleaseSchema>;

export const InvoiceSapSendSchema = z.object({
  transaction: z.literal('output_email'),
  documentNumber: z.string(),
  channel: z.enum(['email']).default('email'),
  sentAt: z.string().datetime().optional(),
  message: z.string().optional(),
});
export type InvoiceSapSend = z.infer<typeof InvoiceSapSendSchema>;

export const InvoiceSapSyncSchema = z.object({
  system: CustomerSapSystemEnum,
  documentType: InvoiceDocumentTypeEnum.optional(),
  customerLookupStatus: InvoiceSapCustomerLookupStatusEnum.default('not_started'),
  vendorLookupStatus: InvoiceSapVendorLookupStatusEnum.default('not_started'),
  materialLookupStatus: InvoiceSapMaterialLookupStatusEnum.default('not_started'),
  matchingGateStatus: InvoiceSapMatchingGateStatusEnum.default('not_started'),
  matchingGateChecks: z.array(InvoiceSapGateCheckSchema).default([]),
  postingStatus: InvoiceSapPostingStatusEnum.default('not_started'),
  releaseStatus: InvoiceSapReleaseStatusEnum.default('not_started'),
  sendStatus: InvoiceSapSendStatusEnum.default('not_started'),
  vendorMatch: InvoiceSapVendorMatchSchema.optional(),
  materialMatches: z.array(InvoiceSapMaterialMatchSchema).optional(),
  materialIssues: z.array(InvoiceSapMaterialIssueSchema).optional(),
  purchaseOrder: InvoiceSapPurchaseOrderSyncSchema.optional(),
  customerOrder: InvoiceSapCustomerOrderSyncSchema.optional(),
  posting: InvoiceSapPostingSchema.optional(),
  release: InvoiceSapReleaseSchema.optional(),
  send: InvoiceSapSendSchema.optional(),
  lastSyncedAt: z.string().datetime().optional(),
  lastError: z.string().optional(),
});
export type InvoiceSapSync = z.infer<typeof InvoiceSapSyncSchema>;

export const SyncInvoiceToSapResponseSchema = z.object({
  id: z.string(),
  sapSync: InvoiceSapSyncSchema,
  updatedAt: z.string().datetime(),
});
export type SyncInvoiceToSapResponse = z.infer<typeof SyncInvoiceToSapResponseSchema>;

export function isActiveSapSystem(
  value: z.infer<typeof CustomerSapSystemEnum>,
): value is z.infer<typeof ActiveSapSystemEnum> {
  return value === 'sap-s4hana' || value === 'sap-erp-6';
}
